class SpeechManager {
  constructor() {
    this.synth = window.speechSynthesis;
    this.speaking = false;
    this.paused = false;
    this.rate = parseFloat(localStorage.getItem('speechRate')) || 1;
    this.currentUtterance = null;
    this.activeMessageId = null;
    this.onSpeechStatusChange = null;
    
    // Queue for managing multiple TTS requests
    this.speechQueue = [];
    this.isProcessingQueue = false;

    this._setupSynthesisEventHandling();
  }

  _setupSynthesisEventHandling() {
    // Global event handlers for synthesis state
    this.synth.addEventListener('voiceschanged', () => {
      // Reset state when voices change
      if (!this.synth.speaking) {
        this.speaking = false;
      }
    });
  }

  // No voice selection needed - using default voice

  setCallbacks(onSpeechStatusChange) {
    this.onSpeechStatusChange = onSpeechStatusChange;
  }



  setRate(rate) {
    this.rate = rate;
    if (this.currentUtterance) {
      this.currentUtterance.rate = rate;
    }
  }

  /**
   * Speaks the given text asynchronously.
   * This method adds the text to a queue and ensures messages are spoken sequentially.
   * 
   * @param {string} text - The text to speak
   * @param {string} messageId - The ID of the message being spoken
   * @returns {Promise} A promise that resolves when speech is complete
   */
  speakText(text, messageId) {
    // Return a promise that resolves when this specific speech item is complete
    return new Promise((resolve, reject) => {
      if (!text || typeof text !== 'string' || text.trim() === '') {
        console.warn('Empty or invalid text provided to speechManager.speakText');
        resolve();
        return;
      }
      
      // Add this speech request to the queue
      this.speechQueue.push({
        text,
        messageId,
        resolve,
        reject
      });
      
      console.log(`Added message ${messageId} to speech queue. Queue length: ${this.speechQueue.length}`);
      
      // Start processing the queue if not already processing
      if (!this.isProcessingQueue) {
        this._processNextInQueue();
      }
    });
  }
  
  /**
   * Process the next item in the speech queue
   * @private
   */
  _processNextInQueue() {
    // If queue is empty or already processing, do nothing
    if (this.speechQueue.length === 0) {
      this.isProcessingQueue = false;
      return;
    }
    
    this.isProcessingQueue = true;
    
    // Get the next item from the queue
    const { text, messageId, resolve, reject } = this.speechQueue[0];
    
    // Set the active message ID
    this.activeMessageId = messageId;
    this.speaking = true;
    
    // Notify that we're speaking when starting
    this.onSpeechStatusChange?.(messageId, 'speaking');
    
    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = this.rate;
      // Using default system voice
      
      utterance.onstart = () => {
        console.log('Speech started for message:', messageId);
        this.speaking = true;
      };
      
      utterance.onend = () => {
        console.log('Speech ended for message:', messageId);
        
        // Remove this item from the queue
        this.speechQueue.shift();
        
        // Reset state for this specific message
        this._resetState();
        
        // Resolve the promise for this specific message
        resolve();
        
        // Process the next item in the queue
        setTimeout(() => this._processNextInQueue(), 100);
      };
      
      utterance.onerror = (error) => {
        console.error('Speech synthesis error:', error);
        
        // Remove this item from the queue
        this.speechQueue.shift();
        
        // Reset state for this specific message
        this._resetState();
        
        // Reject the promise for this specific message
        reject(error);
        
        // Process the next item in the queue
        setTimeout(() => this._processNextInQueue(), 100);
      };
      
      this.currentUtterance = utterance;
      this.synth.speak(utterance);
      
      // Safety timeout in case the speech events don't fire
      setTimeout(() => {
        if (this.speaking && this.activeMessageId === messageId) {
          console.warn('Speech timeout triggered for message:', messageId);
          
          // Remove this item from the queue
          this.speechQueue.shift();
          
          // Reset state for this specific message
          this._resetState();
          
          // Resolve the promise for this specific message
          resolve();
          
          // Process the next item in the queue
          setTimeout(() => this._processNextInQueue(), 100);
        }
      }, 30000); // 30 second timeout
    } catch (error) {
      console.error('Error setting up speech:', error);
      
      // Remove this item from the queue
      this.speechQueue.shift();
      
      // Reset state for this specific message
      this._resetState();
      
      // Reject the promise for this specific message
      reject(error);
      
      // Process the next item in the queue
      setTimeout(() => this._processNextInQueue(), 100);
    }
  }


  _resetState() {
    this.speaking = false;
    this.paused = false;
    this.currentUtterance = null;
    if (this.activeMessageId) {
      this.onSpeechStatusChange?.(this.activeMessageId, 'completed');
      this.activeMessageId = null;
    }
  }

  isSpeaking() {
    // Double-check against both our internal state and the synthesis state
    return this.speaking && this.synth.speaking;
  }





  /**
   * Stops all speech and clears the speech queue
   */
  stop() {
    // Cancel any ongoing speech synthesis
    this.synth.cancel();
    
    // Resolve all pending promises in the queue
    while (this.speechQueue.length > 0) {
      const item = this.speechQueue.shift();
      if (item && item.resolve) {
        item.resolve();
      }
    }
    
    // Reset all state
    this.speaking = false;
    this.paused = false;
    this.currentUtterance = null;
    this.isProcessingQueue = false;
    
    // Notify about completion if there was an active message
    if (this.activeMessageId) {
      this.onSpeechStatusChange?.(this.activeMessageId, 'completed');
      this.activeMessageId = null;
    }
  }


}

const speechManager = new SpeechManager();
export default speechManager;